import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie
} from "recharts";
import "./App.css";
import { useMaximize } from "./hooks/useMaximize";

const COLORS = ["#32D4E0", "#32D74B", "#FFB340", "#BF5AF2", "#FF453A", "#FF9F0A", "#64D2FF", "#30D158"];

// ── Custom Tooltip ─────────────────────────────
function CustomTooltip({ active, payload, label, prefix = "ETB" }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="chart-tooltip">
            <p className="chart-tooltip-label">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }}>
                    {p.name}: {prefix === "ETB" ? `ETB ${Number(p.value).toLocaleString("en", { minimumFractionDigits: 2 })}` : p.value}
                </p>
            ))}
        </div>
    );
}

export default function Analytics({ onClose }) {
    const { toggle, overlayClass, panelClass, icon } = useMaximize(true);
    const [period, setPeriod] = useState("week");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [loading, setLoading] = useState(true);
    const [revenueData, setRevenueData] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [hourlyData, setHourlyData] = useState([]);
    const [marginData, setMarginData] = useState([]);
    const [summary, setSummary] = useState({
        revenue: 0, transactions: 0, avgBasket: 0, topProduct: "—", profit: 0
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (period === "custom") {
            if (customFrom && customTo) fetchAll();
            return;
        }
        fetchAll();
    }, [period, customFrom, customTo]);

    async function fetchAll() {
        setLoading(true);
        const { from, to } = getDateRange(period, customFrom, customTo);

        await Promise.all([
            fetchRevenue(from, to),
            fetchTopProducts(from, to),
            fetchCategories(from, to),
            fetchHourly(from, to),
            fetchMargins(),
        ]);
        setLoading(false);
    }

    function toIsoAtUtcStartOfDay(dateString) {
        if (!dateString) return null;
        const d = new Date(`${dateString}T00:00:00.000Z`);
        return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }

    function toIsoAtUtcEndOfDay(dateString) {
        if (!dateString) return null;
        const d = new Date(`${dateString}T23:59:59.999Z`);
        return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }

    function getDefaultCustomRange() {
        const end = new Date();
        const start = new Date(end);
        start.setDate(start.getDate() - 6); // last 7 days inclusive

        const toDateInputValue = (d) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
        };

        return { from: toDateInputValue(start), to: toDateInputValue(end) };
    }

    function getDateRange(p, fromDate, toDate) {
        const now = new Date();
        if (p === "today") {
            const start = new Date(now);
            start.setUTCHours(0, 0, 0, 0);
            return { from: start.toISOString(), to: null };
        }
        if (p === "week") {
            const start = new Date(now);
            start.setUTCDate(start.getUTCDate() - 7);
            return { from: start.toISOString(), to: null };
        }
        if (p === "month") {
            const start = new Date(now);
            start.setUTCDate(start.getUTCDate() - 30);
            return { from: start.toISOString(), to: null };
        }
        if (p === "year") {
            const start = new Date(now);
            start.setUTCFullYear(start.getUTCFullYear() - 1);
            return { from: start.toISOString(), to: null };
        }
        if (p === "custom") {
            const from = toIsoAtUtcStartOfDay(fromDate);
            const to = toIsoAtUtcEndOfDay(toDate);
            if (from && to && from > to) {
                return { from: to, to: from };
            }
            return { from, to };
        }
        return { from: null, to: null };
    }

    function handlePeriodChange(next) {
        setPeriod(next);
        if (next === "custom" && (!customFrom || !customTo)) {
            const def = getDefaultCustomRange();
            setCustomFrom(def.from);
            setCustomTo(def.to);
        }
    }

    // ── Revenue over time ──────────────────────────
    async function fetchRevenue(from, to) {
        let query = supabase
            .from("sales_transactions")
            .select("total_amount, created_at")
            .eq("status", "completed");
        if (from) query = query.gte("created_at", from);
        if (to) query = query.lte("created_at", to);
        const { data: sales, error: salesError } = await query;
        logSupabaseError("Analytics: fetchRevenue sales_transactions", salesError);
        if (!sales) return;

        // Fetch sale items with cost data for profit calculation
        let itemQuery = supabase
            .from("sale_items")
            .select("quantity, line_total, unit_price, product_id, created_at, products(cost_price)");
        if (from) itemQuery = itemQuery.gte("created_at", from);
        if (to) itemQuery = itemQuery.lte("created_at", to);
        const { data: items, error: itemsError } = await itemQuery;
        logSupabaseError("Analytics: fetchRevenue sale_items", itemsError);

        // Group by date or month depending on period
        const grouped = {};
        sales.forEach((s) => {
            const d = new Date(s.created_at);
            const key = period === "year"
                ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
                : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            if (!grouped[key]) grouped[key] = { date: key, revenue: 0, cost: 0, transactions: 0 };
            grouped[key].revenue += Number(s.total_amount);
            grouped[key].transactions += 1;
        });

        // Add cost data for profit
        if (items) {
            items.forEach((item) => {
                const d = new Date(item.created_at);
                const key = period === "year"
                    ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
                    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                if (grouped[key]) {
                    const costPrice = Number(item.products?.cost_price ?? 0);
                    grouped[key].cost += costPrice * Number(item.quantity);
                }
            });
        }

        const sorted = Object.values(grouped).map((g) => ({
            ...g,
            profit: g.revenue - g.cost,
        }));

        setRevenueData(sorted);

        const totalRevenue = sales.reduce((s, t) => s + Number(t.total_amount), 0);
        const totalCost = items
            ? items.reduce((s, i) => s + Number(i.products?.cost_price ?? 0) * Number(i.quantity), 0)
            : 0;

        setSummary((prev) => ({
            ...prev,
            revenue: totalRevenue,
            profit: totalRevenue - totalCost,
            transactions: sales.length,
            avgBasket: sales.length ? totalRevenue / sales.length : 0,
        }));
    }

    // ── Top products ───────────────────────────────
    async function fetchTopProducts(from, to) {
        let query = supabase
            .from("sale_items")
            .select("product_name, quantity, line_total, created_at");
        if (from) query = query.gte("created_at", from);
        if (to) query = query.lte("created_at", to);

        const { data, error } = await query;
        logSupabaseError("Analytics: fetchTopProducts sale_items", error);
        if (!data) return;

        const grouped = {};
        data.forEach((item) => {
            const name = item.product_name;
            if (!grouped[name]) grouped[name] = { name, quantity: 0, revenue: 0 };
            grouped[name].quantity += Number(item.quantity);
            grouped[name].revenue += Number(item.line_total);
        });

        const sorted = Object.values(grouped)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);

        setTopProducts(sorted);
        if (sorted.length > 0) {
            setSummary((prev) => ({ ...prev, topProduct: sorted[0].name }));
        }
    }

    // ── Category breakdown ─────────────────────────
    async function fetchCategories(from, to) {
        let query = supabase
            .from("sale_items")
            .select(`line_total, created_at, products(category_id, categories(name, color, icon))`);
        if (from) query = query.gte("created_at", from);
        if (to) query = query.lte("created_at", to);

        const { data, error } = await query;
        logSupabaseError("Analytics: fetchCategories sale_items", error);
        if (!data) return;

        const grouped = {};
        data.forEach((item) => {
            const cat = item.products?.categories;
            const name = cat?.name ?? "Uncategorized";
            const color = cat?.color ?? "#5A5A60";
            const icon = cat?.icon ?? "📦";
            if (!grouped[name]) grouped[name] = { name, color, icon, value: 0 };
            grouped[name].value += Number(item.line_total);
        });

        setCategoryData(Object.values(grouped).sort((a, b) => b.value - a.value));
    }

    // ── Hourly heatmap ─────────────────────────────
    async function fetchHourly(from, to) {
        let query = supabase
            .from("sales_transactions")
            .select("created_at, total_amount")
            .eq("status", "completed");
        if (from) query = query.gte("created_at", from);
        if (to) query = query.lte("created_at", to);

        const { data, error } = await query;
        logSupabaseError("Analytics: fetchHourly sales_transactions", error);
        if (!data) return;

        const hours = Array.from({ length: 24 }, (_, h) => ({
            hour: `${h.toString().padStart(2, "0")}:00`,
            transactions: 0,
            revenue: 0,
        }));

        data.forEach((s) => {
            const h = new Date(s.created_at).getHours();
            hours[h].transactions += 1;
            hours[h].revenue += Number(s.total_amount);
        });

        setHourlyData(hours.filter((h) => h.transactions > 0));
    }

    // ── Profit margin ──────────────────────────────
    async function fetchMargins() {
        const { data: products, error: productsError } = await supabase
            .from("products")
            .select("id, name, cost_price, selling_price, stock_qty")
            .eq("is_active", true)
            .order("selling_price", { ascending: false })
            .limit(15);
        logSupabaseError("Analytics: fetchMargins products", productsError);

        if (!products) return;

        // Get price history for each product
        const productIds = products.map((p) => p.id);
        const { data: history, error: historyError } = await supabase
            .from("price_history")
            .select("product_id, price_type, old_price, new_price, created_at")
            .in("product_id", productIds)
            .order("created_at", { ascending: false });
        logSupabaseError("Analytics: fetchMargins price_history", historyError);

        const withMargin = products.map((p) => {
            const cost = Number(p.cost_price ?? 0);
            const price = Number(p.selling_price ?? 0);

            // Get ALL price changes for this product
            const productHistory = (history ?? []).filter((h) => h.product_id === p.id);

            // Get cost history — find the most recent change that has an old_price
            const costHistory = productHistory
                .filter((h) => h.price_type === "cost" && h.old_price !== null)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            // Get selling history — find the most recent change that has an old_price  
            const sellingHistory = productHistory
                .filter((h) => h.price_type === "selling" && h.old_price !== null)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            const prevCost = costHistory[0]?.old_price ?? null;
            const prevSelling = sellingHistory[0]?.old_price ?? null;

            return {
                id: p.id,
                name: p.name.length > 18 ? p.name.slice(0, 18) + "…" : p.name,
                cost,
                price,
                profit: price - cost,
                margin: price > 0 && cost > 0
                    ? Math.round(((price - cost) / price) * 100)
                    : 0,
                stock: p.stock_qty,
                prevCost,
                prevSelling,
                hasCost: cost > 0,
            };
        });

        // Sort by margin descending
        setMarginData(withMargin.sort((a, b) => b.margin - a.margin));
    }

    const PERIODS = [
        { key: "today", label: "Today" },
        { key: "week", label: "Last 7 days" },
        { key: "month", label: "Last 30 days" },
        { key: "year", label: "Last Year" },
        { key: "custom", label: "Custom" },
    ];

    return (
        <div className={overlayClass}>
            <div className={panelClass} style={{ maxWidth: "1200px" }}>

                {/* ── Header ── */}
                <div className="analytics-header">
                    <div>
                        <h1 className="analytics-title">📈 Analytics Dashboard</h1>
                        <p className="analytics-sub">Sales insights & performance metrics</p>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {/* Period selector */}
                        <div className="filter-tabs">
                            {PERIODS.map((p) => (
                                <button
                                    key={p.key}
                                    className={`filter-tab ${period === p.key ? "active" : ""}`}
                                    onClick={() => handlePeriodChange(p.key)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {period === "custom" && (
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <input
                                    type="date"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                    className="input"
                                    aria-label="From date"
                                    style={{ height: "34px", padding: "6px 10px" }}
                                />
                                <span style={{ color: "var(--text3)", fontSize: "12px" }}>→</span>
                                <input
                                    type="date"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                    className="input"
                                    aria-label="To date"
                                    style={{ height: "34px", padding: "6px 10px" }}
                                />
                            </div>
                        )}
                        <button className="maximize-btn" onClick={toggle} title={icon === "⊞" ? "Maximize" : "Restore"}>
                            {icon}
                        </button>
                    </div>
                    <button className="report-close" onClick={onClose}>✕</button>
                </div>

                {loading ? (
                    <div className="analytics-loading">
                        <div className="analytics-spinner" />
                        <p>Loading analytics...</p>
                    </div>
                ) : (
                    <div className="analytics-body">

                            {/* ── KPI Row ── */}
                            <div className="analytics-kpi-row">
                                <div className="analytics-kpi">
                                    <p className="analytics-kpi-icon">💰</p>
                                    <p className="analytics-kpi-label">Total Revenue</p>
                                    <p className="analytics-kpi-value" style={{ color: "var(--cyan)" }}>
                                        ETB {summary.revenue.toLocaleString("en", { minimumFractionDigits: 2 })}
                                    </p>
                                    <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                        Money received from sales
                                    </p>
                                </div>
                                <div className="analytics-kpi">
                                    <p className="analytics-kpi-icon">📈</p>
                                    <p className="analytics-kpi-label">Total Profit</p>
                                    <p className="analytics-kpi-value" style={{ color: summary.profit >= 0 ? "var(--green)" : "var(--red)" }}>
                                        ETB {summary.profit.toLocaleString("en", { minimumFractionDigits: 2 })}
                                    </p>
                                    <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                        Revenue minus cost of goods
                                    </p>
                                </div>
                                <div className="analytics-kpi">
                                    <p className="analytics-kpi-icon">🧾</p>
                                    <p className="analytics-kpi-label">Transactions</p>
                                    <p className="analytics-kpi-value" style={{ color: "var(--amber)" }}>
                                        {summary.transactions}
                                    </p>
                                    <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                        Total completed sales
                                    </p>
                                </div>
                                <div className="analytics-kpi">
                                    <p className="analytics-kpi-icon">🛒</p>
                                    <p className="analytics-kpi-label">Avg. Basket</p>
                                    <p className="analytics-kpi-value" style={{ color: "var(--purple)" }}>
                                        ETB {summary.avgBasket.toFixed(2)}
                                    </p>
                                    <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                        Average per transaction
                                    </p>
                                </div>
                            </div>

                        {/* ── Charts Grid ── */}
                        <div className="analytics-grid">

                                {/* Revenue + Profit Chart */}
                                <div className="analytics-card full-width">
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                                        <p className="analytics-card-title" style={{ margin: 0 }}>
                                            📊 Revenue & Profit — {period === "custom" ? "Custom Range" : period === "year" ? "Monthly" : period === "week" ? "Daily (7 days)" : period === "month" ? "Daily (30 days)" : "Today"}
                                        </p>
                                        <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "12px", height: "3px", background: "var(--cyan)", display: "inline-block", borderRadius: "2px" }} />
                                                <span style={{ color: "var(--text3)" }}>Revenue</span>
                                            </span>
                                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "12px", height: "3px", background: "var(--green)", display: "inline-block", borderRadius: "2px" }} />
                                                <span style={{ color: "var(--text3)" }}>Profit</span>
                                            </span>
                                        </div>
                                    </div>
                                    {revenueData.length === 0 ? (
                                        <p className="analytics-empty">No data for this period</p>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={220}>
                                            <AreaChart data={revenueData}>
                                                <defs>
                                                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#32D4E0" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#32D4E0" stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id="profGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#32D74B" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#32D74B" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" />
                                                <XAxis dataKey="date" tick={{ fill: "#5A5A60", fontSize: 11 }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: "#5A5A60", fontSize: 11 }} axisLine={false} tickLine={false}
                                                    tickFormatter={(v) => `${v.toLocaleString()}`} />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Area type="monotone" dataKey="revenue" name="Revenue"
                                                    stroke="#32D4E0" strokeWidth={2} fill="url(#revGrad)" />
                                                <Area type="monotone" dataKey="profit" name="Profit"
                                                    stroke="#32D74B" strokeWidth={2} fill="url(#profGrad)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}

                                    {/* Summary table below chart */}
                                    {revenueData.length > 0 && (
                                        <div className="report-table-wrap" style={{ marginTop: "14px" }}>
                                            <table className="report-table" style={{ fontSize: "12px" }}>
                                                <thead>
                                                    <tr>
                                                        <th>{period === "year" ? "Month" : "Date"}</th>
                                                        <th>Revenue</th>
                                                        <th>Est. Profit</th>
                                                        <th>Transactions</th>
                                                        <th>Margin</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {revenueData.map((row, i) => {
                                                        const margin = row.revenue > 0
                                                            ? Math.round((row.profit / row.revenue) * 100) : 0;
                                                        return (
                                                            <tr key={i}>
                                                                <td style={{ fontWeight: 600 }}>{row.date}</td>
                                                                <td style={{ fontFamily: "var(--mono)", color: "var(--cyan)" }}>
                                                                    ETB {row.revenue.toLocaleString("en", { minimumFractionDigits: 2 })}
                                                                </td>
                                                                <td style={{ fontFamily: "var(--mono)", color: row.profit >= 0 ? "var(--green)" : "var(--red)", fontWeight: 600 }}>
                                                                    ETB {row.profit.toLocaleString("en", { minimumFractionDigits: 2 })}
                                                                </td>
                                                                <td style={{ color: "var(--text2)" }}>{row.transactions}</td>
                                                                <td>
                                                                    <span className={`margin-badge ${margin >= 30 ? "good" : margin >= 15 ? "ok" : "low"}`}>
                                                                        {margin}%
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td className="total-label">TOTAL</td>
                                                        <td style={{ fontFamily: "var(--mono)", color: "var(--cyan)", fontWeight: 700 }}>
                                                            ETB {summary.revenue.toLocaleString("en", { minimumFractionDigits: 2 })}
                                                        </td>
                                                        <td style={{ fontFamily: "var(--mono)", color: summary.profit >= 0 ? "var(--green)" : "var(--red)", fontWeight: 700 }}>
                                                            ETB {summary.profit.toLocaleString("en", { minimumFractionDigits: 2 })}
                                                        </td>
                                                        <td style={{ color: "var(--text2)", fontWeight: 700 }}>{summary.transactions}</td>
                                                        <td>
                                                            <span className={`margin-badge ${summary.revenue > 0 && Math.round((summary.profit / summary.revenue) * 100) >= 30 ? "good" : "ok"}`}>
                                                                {summary.revenue > 0 ? Math.round((summary.profit / summary.revenue) * 100) : 0}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>

                            {/* Top Products */}
                            <div className="analytics-card full-width">
                                <p className="analytics-card-title">🏆 Top Products by Revenue</p>
                                {topProducts.length === 0 ? (
                                    <p className="analytics-empty">No data yet</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={Math.max(topProducts.length * 32, 120)}>
                                        <BarChart data={topProducts} layout="vertical" barCategoryGap="25%">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" horizontal={false} />
                                            <XAxis type="number" tick={{ fill: "#5A5A60", fontSize: 10 }} axisLine={false}
                                                tickFormatter={(v) => `ETB ${v.toLocaleString()}`} />
                                            <YAxis type="category" dataKey="name" tick={{ fill: "#98989F", fontSize: 11 }}
                                                axisLine={false} tickLine={false} width={100} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} maxBarSize={22}>
                                                {topProducts.map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                            {/* Category Pie */}
                            <div className="analytics-card">
                                <p className="analytics-card-title">🏷️ Category Breakdown</p>
                                {categoryData.length === 0 ? (
                                    <p className="analytics-empty">No data yet</p>
                                ) : (
                                    <>
                                        <ResponsiveContainer width="100%" height={180}>
                                            <PieChart>
                                                <Pie
                                                    data={categoryData}
                                                    cx="50%" cy="50%"
                                                    innerRadius={50} outerRadius={80}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                >
                                                    {categoryData.map((entry, i) => (
                                                        <Cell key={i} fill={entry.color ?? COLORS[i % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(v) => [`ETB ${Number(v).toFixed(2)}`, "Revenue"]} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="cat-legend">
                                            {categoryData.slice(0, 5).map((cat, i) => (
                                                <div key={i} className="cat-legend-item">
                                                    <span className="cat-legend-dot" style={{ background: cat.color ?? COLORS[i % COLORS.length] }} />
                                                    <span className="cat-legend-name">{cat.icon} {cat.name}</span>
                                                    <span className="cat-legend-value">ETB {cat.value.toFixed(0)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Hourly Heatmap */}
                            <div className="analytics-card">
                                <p className="analytics-card-title">⏰ Busiest Hours</p>
                                {hourlyData.length === 0 ? (
                                    <p className="analytics-empty">No data yet</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={hourlyData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                                            <XAxis dataKey="hour" tick={{ fill: "#5A5A60", fontSize: 10 }}
                                                axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fill: "#5A5A60", fontSize: 10 }} axisLine={false} tickLine={false} />
                                            <Tooltip content={<CustomTooltip prefix="count" />} />
                                            <Bar dataKey="transactions" name="Transactions" radius={[4, 4, 0, 0]}>
                                                {hourlyData.map((entry, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={entry.transactions > 5 ? "#32D4E0"
                                                            : entry.transactions > 2 ? "#FFB340"
                                                                : "#3A3A3C"}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                                {/* Profit Margin */}
                                <div className="analytics-card full-width">
                                    <p className="analytics-card-title">💹 Profit Margin Analysis</p>
                                    {marginData.filter((p) => p.hasCost).length === 0 ? (
                                        <div className="analytics-empty">
                                            <p>💡 No cost price data yet</p>
                                            <p style={{ fontSize: "12px", marginTop: "4px", color: "var(--text3)" }}>
                                                Cost prices are recorded automatically when you receive stock in the Warehouse.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="report-table-wrap">
                                            <table className="report-table" style={{ marginTop: 0 }}>
                                                <thead>
                                                    <tr>
                                                        <th>Product</th>
                                                        <th>Cost Price</th>
                                                        <th>Prev. Cost</th>
                                                        <th>Selling Price</th>
                                                        <th>Prev. Selling</th>
                                                        <th>Profit / Unit</th>
                                                        <th>Margin %</th>
                                                        <th>Stock</th>
                                                        <th>Health</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {marginData.filter((p) => p.hasCost).map((p, i) => {
                                                        const health = p.margin >= 30 ? "good"
                                                            : p.margin >= 15 ? "ok" : "low";
                                                        const costChanged = p.prevCost !== null && p.prevCost !== p.cost;
                                                        const sellingChanged = p.prevSelling !== null && p.prevSelling !== p.price;
                                                        return (
                                                            <tr key={i}>
                                                                <td style={{ fontWeight: 600 }}>{p.name}</td>

                                                                {/* Cost price with change indicator */}
                                                                <td>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                        <span style={{ fontFamily: "var(--mono)", color: "var(--text2)" }}>
                                                                            ETB {p.cost.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {p.prevCost !== null ? (
                                                                        <span style={{
                                                                            fontFamily: "var(--mono)",
                                                                            fontSize: "12px",
                                                                            color: costChanged
                                                                                ? p.cost > p.prevCost ? "var(--red)" : "var(--green)"
                                                                                : "var(--text3)"
                                                                        }}>
                                                                            ETB {Number(p.prevCost).toFixed(2)}
                                                                            {costChanged && (
                                                                                <span style={{ marginLeft: "4px" }}>
                                                                                    {p.cost > p.prevCost ? "↑" : "↓"}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ color: "var(--text3)", fontSize: "12px" }}>—</span>
                                                                    )}
                                                                </td>

                                                                {/* Selling price with change indicator */}
                                                                <td>
                                                                    <span style={{ fontFamily: "var(--mono)", color: "var(--cyan)" }}>
                                                                        ETB {p.price.toFixed(2)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {p.prevSelling !== null ? (
                                                                        <span style={{
                                                                            fontFamily: "var(--mono)",
                                                                            fontSize: "12px",
                                                                            color: sellingChanged
                                                                                ? p.price > p.prevSelling ? "var(--green)" : "var(--red)"
                                                                                : "var(--text3)"
                                                                        }}>
                                                                            ETB {Number(p.prevSelling).toFixed(2)}
                                                                            {sellingChanged && (
                                                                                <span style={{ marginLeft: "4px" }}>
                                                                                    {p.price > p.prevSelling ? "↑" : "↓"}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <span style={{ color: "var(--text3)", fontSize: "12px" }}>—</span>
                                                                    )}
                                                                </td>

                                                                {/* Profit */}
                                                                <td>
                                                                    <span style={{
                                                                        fontFamily: "var(--mono)",
                                                                        fontWeight: 600,
                                                                        color: p.profit > 0 ? "var(--green)" : "var(--red)"
                                                                    }}>
                                                                        ETB {p.profit.toFixed(2)}
                                                                    </span>
                                                                </td>

                                                                {/* Margin bar */}
                                                                <td>
                                                                    <div className="margin-bar-wrap">
                                                                        <div
                                                                            className="margin-bar"
                                                                            style={{
                                                                                width: `${Math.min(Math.max(p.margin, 0), 100)}%`,
                                                                                background: health === "good" ? "var(--green)"
                                                                                    : health === "ok" ? "var(--amber)"
                                                                                        : "var(--red)",
                                                                            }}
                                                                        />
                                                                        <span className="margin-pct">{p.margin}%</span>
                                                                    </div>
                                                                </td>

                                                                {/* Stock */}
                                                                <td>
                                                                    <span style={{ fontFamily: "var(--mono)", color: "var(--text2)" }}>
                                                                        {p.stock}
                                                                    </span>
                                                                </td>

                                                                {/* Health */}
                                                                <td>
                                                                    <span className={`margin-badge ${health}`}>
                                                                        {health === "good" ? "✓ Good"
                                                                            : health === "ok" ? "~ Fair"
                                                                                : "↓ Low"}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}